import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";

// ─── table header style ───────────────────────────────────────────────────────
const thSx = {
  background: "#04343a",
  color: "#fff",
  fontWeight: 700,
  fontSize: "13px",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.15)",
  "&:last-child": { borderRight: "none" },
};

const tdSx = {
  borderRight: "1px solid #e2e8f0",
  "&:last-child": { borderRight: "none" },
};

// ─── empty states ─────────────────────────────────────────────────────────────
const emptyTrade = () => ({ label: "", value: "", is_positive: true });

// ─── main page ────────────────────────────────────────────────────────────────
const PerformanceCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  // symbols for autocomplete
  const [symbols, setSymbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);

  // create card dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);

  // add trade dialog
  const [tradeOpen, setTradeOpen] = useState(false);
  const [tradeCard, setTradeCard] = useState(null);
  const [trade, setTrade] = useState(emptyTrade());
  const [addingTrade, setAddingTrade] = useState(false);

  // edit card dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [editTitle, setEditTitle] = useState(""); 
  const [editTrades, setEditTrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const originalTradesRef = useRef([]);

  console.log(editTrades, "editTrades")

  // delete card
  const [deletingId, setDeletingId] = useState(null);

  // ── fetch all cards ──────────────────────────────────────────────────────
  const fetchCards = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/appdashboard/performance-card/",
      });
      setCards(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCards(); }, []);

  const fetchSymbols = async () => {
    setSymbolsLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: "/home/symbols/list" });
setSymbols(res?.data?.response?.[0]?.data || []);
    } catch (err) {
      toast.error("Failed to load symbols");
    } finally {
      setSymbolsLoading(false);
    }
  };

  useEffect(() => { if (tradeOpen || editOpen) fetchSymbols(); }, [tradeOpen, editOpen]);

  // ── create card ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      await axiosConfig({
        method: "post",
        url: "/appdashboard/performance-card/",
        data: { title },
      });
      toast.success("Performance card created");
      setCreateOpen(false);
      setTitle("");
      fetchCards();
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  // ── open add trade dialog ────────────────────────────────────────────────
  const openAddTrade = (card) => {
    setTradeCard(card);
    setTrade({ ...emptyTrade(), dashboard: card.id });
    setTradeOpen(true);
  };

  // ── add trade ────────────────────────────────────────────────────────────
  const handleAddTrade = async () => {
    if (!trade.label) return toast.error("Label (Stock ID) is required");
    if (!trade.value) return toast.error("Value is required");
    setAddingTrade(true);
    try {
      await axiosConfig({
        method: "post",
        url: `/appdashboard/peformance-card-stocks/${tradeCard.id}/`,
        data: {
          dashboard: tradeCard.id,
          label: Number(trade.label),
          value: trade.value,
          is_positive: trade.is_positive,
        },
      });
      toast.success("Trade added");
      setTradeOpen(false);
      setTrade(emptyTrade());
      fetchCards();
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Failed to add trade");
    } finally {
      setAddingTrade(false);
    }
  };

  // ── open / close edit dialog ─────────────────────────────────────────────
  const openEdit = (card) => {
    setEditCard(card);
    setEditTitle(card.title);
    const snapshot = (card.trades || []).map((t) => ({ ...t }));
    originalTradesRef.current = snapshot;
    setEditTrades(snapshot.map((t) => ({ ...t })));
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (!saving) {
      setEditOpen(false);
      setEditCard(null);
      setEditTitle("");
      setEditTrades([]);
    }
  };

  // ── save edit ────────────────────────────────────────────────────────────
  const handleEditSave = async () => {
    if (!editTitle.trim()) return toast.error("Title is required");

    const origMap = Object.fromEntries(originalTradesRef.current.map((t) => [t.id, t]));
    const modifiedTrades = editTrades.filter((t) => {
      const orig = origMap[t.id];
      if (!orig) return true;
      return (
        Number(t.label) !== Number(orig.label) ||
        t.value !== orig.value ||
        t.is_positive !== orig.is_positive
      );
    });

    setSaving(true);
    try {
      await Promise.all(
        modifiedTrades.map((t) =>
          axiosConfig({
            method: "put",
            url: `/appdashboard/peformance-card-stocks/${t.id}/`,
            data: {
              title: editTitle,
              dashboard: editCard.id,
              label: Number(t.label),
              value: t.value,
              is_positive: t.is_positive,
            },
          })
        )
      );
      toast.success("Card updated");
      closeEdit();
      fetchCards();
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  // ── delete card ──────────────────────────────────────────────────────────
  const handleDelete = async (cardId) => {
    setDeletingId(cardId);
    try {
      await axiosConfig({
        method: "delete",
        url: `/appdashboard/performance-card/${cardId}/`,
      });
      toast.success("Card deleted");
      fetchCards();
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const closeCreate = () => { if (!creating) { setCreateOpen(false); setTitle(""); } };
  const closeTrade  = () => { if (!addingTrade) { setTradeOpen(false); setTradeCard(null); setTrade(emptyTrade()); } };

  return (
    <>
      <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

        {/* Page Header */}
        <Box sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <BarChartIcon sx={{ color: "#fff", fontSize: 32 }} />
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
                Performance Cards
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
                Manage performance showcase cards and their trades
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            Create Card
          </Button>
        </Box>

        {/* Table */}
        <Box sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}>
          <TableContainer>
            <Table sx={{ borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 48 }}>SNo.</TableCell>
                  <TableCell sx={thSx}>Title</TableCell>
                  <TableCell sx={thSx}>Trades</TableCell>
                  <TableCell sx={{ ...thSx, width: 120, textAlign: "center" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : !Array.isArray(cards) || cards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                        No performance cards yet. Create one!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cards.map((card, idx) => (
                    <TableRow
                      key={card.id}
                      sx={{
                        "&:hover": { background: "#f8fafc" },
                        transition: "background 0.15s",
                        borderBottom: "1px solid #e2e8f0",
                        verticalAlign: "top",
                      }}
                    >
                      {/* SNo */}
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", fontSize: "13px", fontWeight: 500, pt: 1.5 }}>
                        {idx + 1}
                      </TableCell>

                      {/* Title */}
                      <TableCell sx={{ ...tdSx, pt: 1.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#04343a" }}>
                          {card.title}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.3 }}>
                          ID: {card.id}
                        </Typography>
                      </TableCell> 

                      {/* Trades */}
                      <TableCell sx={{ ...tdSx, py: 1.5 }}>
                        {(card.trades || []).length === 0 ? (
                          <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>No trades yet</Typography>
                        ) : (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {(card.trades || []).map((t, i) => {
                              const palettes = [
                                { bg: "#f0fdf4", border: "#bbf7d0", label: "#016551", value: "#014d3d" },
                                { bg: "#eff6ff", border: "#bfdbfe", label: "#1d4ed8", value: "#1e3a8a" },
                                { bg: "#fffbeb", border: "#fde68a", label: "#b45309", value: "#92400e" },
                                { bg: "#fdf4ff", border: "#e9d5ff", label: "#7c3aed", value: "#4c1d95" },
                                { bg: "#fff1f2", border: "#fecdd3", label: "#be123c", value: "#881337" },
                                { bg: "#f0f9ff", border: "#bae6fd", label: "#0369a1", value: "#0c4a6e" },
                              ];
                              const p = t.is_positive
                                ? palettes[i % (palettes.length - 1)]
                                : palettes[4];
                              return (
                                <Box
                                  key={t.id ?? i}
                                  sx={{
                                    background: p.bg,
                                    border: `1px solid ${p.border}`,
                                    borderRadius: "10px",
                                    px: 1.5,
                                    py: 1,
                                    minWidth: 90,
                                  }}
                                >
                                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: p.label, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.3 }}>
                                    {t.label_name || t.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: "16px", fontWeight: 800, color: p.value, lineHeight: 1.2 }}>
                                    {t.value}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ ...tdSx, textAlign: "center", pt: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
                          <Tooltip title="Edit Card">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(card)}
                              sx={{
                                color: "#64748b",
                                background: "#f1f5f9",
                                "&:hover": { background: "#04343a", color: "#fff" },
                                transition: "all 0.2s",
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Trade">
                            <IconButton
                              size="small"
                              onClick={() => openAddTrade(card)}
                              sx={{
                                color: "#016651",
                                background: "#f0faf7",
                                "&:hover": { background: "#016551", color: "#fff" },
                                transition: "all 0.2s",
                              }}
                            >
                              <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Card">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(card.id)}
                              disabled={deletingId === card.id}
                              sx={{
                                color: "#dc2626",
                                background: "#fff5f5",
                                "&:hover": { background: "#dc2626", color: "#fff" },
                                "&:disabled": { opacity: 0.5 },
                                transition: "all 0.2s",
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* ── Create Card Dialog ────────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={closeCreate} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "17px" }}>
          Create Performance Card
        </DialogTitle>
        <IconButton onClick={closeCreate} sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Title"
            fullWidth
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. April's Premium Trades"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreate} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Card Dialog ─────────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "17px", pb: 1.5 }}>
          Edit Performance Card
          {editCard && (
            <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.6)", mt: 0.3 }}>
              ID: {editCard.id}
            </Typography>
          )}
        </DialogTitle>
        <IconButton onClick={closeEdit} sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Title */}
          <TextField
            label="Title"
            fullWidth
            size="small"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />

          {/* Trades */}
          {editTrades.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#04343a", mb: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Trades
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {editTrades.map((t, i) => (
                  <Box key={i}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
                        Trade {i + 1}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1, borderLeft: "2px solid #e2e8f0" }}>
                      <Autocomplete
                        options={Array.isArray(symbols) ? symbols : []}
                        loading={symbolsLoading}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={Array.isArray(symbols) ? (symbols.find((s) => s.id === t.label) || null) : null}
                        onChange={(_, selected) =>
                          setEditTrades((prev) =>
                            prev.map((item, idx) =>
                              idx === i ? { ...item, label: selected ? selected.id : "" } : item
                            )
                          )
                        }
                        renderOption={(props, opt) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{opt.name}</Typography>
                              <Typography sx={{ fontSize: "11px", color: "#64748b" }}>{opt.company_name}</Typography>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Symbol" size="small" placeholder="Search symbol..." />
                        )}
                      />
                      <TextField
                        label="Value"
                        fullWidth
                        size="small"
                        value={t.value}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/%/g, "");
                          setEditTrades((prev) =>
                            prev.map((item, idx) =>
                              idx === i ? { ...item, value: raw ? raw + "%" : "" } : item
                            )
                          );
                        }}
                        placeholder="e.g. 10.5%"
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Switch
                          checked={t.is_positive}
                          onChange={(e) =>
                            setEditTrades((prev) =>
                              prev.map((item, idx) =>
                                idx === i ? { ...item, is_positive: e.target.checked } : item
                              )
                            )
                          }
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
                          }}
                        />
                        <Typography sx={{ fontSize: "13px", color: t.is_positive ? "#016551" : "#dc2626", fontWeight: 600 }}>
                          {t.is_positive ? "Positive" : "Negative"}
                        </Typography>
                      </Box>
                    </Box>
                    {i < editTrades.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Trade Dialog ──────────────────────────────────────────────── */}
      <Dialog open={tradeOpen} onClose={closeTrade} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "17px" }}>
          Add Trade
          {tradeCard && (
            <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.6)", mt: 0.3 }}>
              {tradeCard.title}
            </Typography>
          )}
        </DialogTitle>
        <IconButton onClick={closeTrade} sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Autocomplete
            options={Array.isArray(symbols) ? symbols : []}
            loading={symbolsLoading}
            getOptionLabel={(opt) => opt.name || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, selected) => setTrade((p) => ({ ...p, label: selected ? selected.id : "" }))}
            renderOption={(props, opt) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{opt.name}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#64748b" }}>{opt.company_name}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Symbol"
                size="small"
                placeholder="Search symbol..."
              />
            )}
          />
          <TextField
            label="Value"
            fullWidth
            size="small"
            value={trade.value}
            onChange={(e) => {
              const raw = e.target.value.replace(/%/g, "");
              setTrade((p) => ({ ...p, value: raw ? raw + "%" : "" }));
            }}
            placeholder="e.g. 10.5%"
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={trade.is_positive}
              onChange={(e) => setTrade((p) => ({ ...p, is_positive: e.target.checked }))}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
              }}
            />
            <Typography sx={{ fontSize: "13px", color: trade.is_positive ? "#016551" : "#dc2626", fontWeight: 600 }}>
              {trade.is_positive ? "Positive" : "Negative"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeTrade} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddTrade}
            disabled={addingTrade}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {addingTrade ? "Adding..." : "Add Trade"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PerformanceCards;
